<template>
  <header
    class="top-nav"
    role="navigation"
    aria-label="Benutzermenü"
  >
    <button
      class="burger"
      type="button"
      :aria-expanded="sidebarOpen ? 'true' : 'false'"
      aria-controls="app-drawer"
      @click="emit('toggle-sidebar')"
    >
      <i
        class="pi pi-bars"
        aria-hidden="true"
      />
      <span class="sr-only">Menü</span>
    </button>
    <div
      v-if="isAdmin"
      class="roles"
      aria-label="Online-Nutzer"
    >
      <div
        class="roles-group parents"
        aria-label="Eltern"
      >
        <span
          v-for="u in parentsOnline"
          :key="'p-' + u.id"
          class="badge badge-parent"
          :class="{ 'is-stale': !u.isRecent }"
          :title="u.display_name"
          :aria-label="u.display_name"
        >{{ u.name_code || initialsOf(u.display_name) }}</span>
      </div>
      <div
        class="roles-sep"
        aria-hidden="true"
      />
      <div
        class="roles-group teachers"
        aria-label="Lehrer"
      >
        <span
          v-for="u in teachersOnline"
          :key="'t-' + u.id"
          class="badge badge-teacher"
          :class="{ 'is-stale': !u.isRecent }"
          :title="u.display_name"
          :aria-label="u.display_name"
        >{{ u.name_code || initialsOf(u.display_name) }}</span>
      </div>
    </div>
    <div class="grow" />

    <span class="top-nav-help-slot" />

    <div
      ref="rootEl"
      class="profile"
    >
      <button
        class="profile-btn"
        aria-haspopup="menu"
        :aria-expanded="menuOpen.toString()"
        :aria-controls="menuOpen ? 'user-menu' : null"
        @click="toggle"
      >
        <span
          class="avatar"
          aria-hidden="true"
        >{{ initials }}</span>
        <span class="name">{{ displayName }}</span>
        <i
          class="pi pi-chevron-down"
          aria-hidden="true"
        />
      </button>

      <ul
        v-if="menuOpen"
        id="user-menu"
        class="menu"
        role="menu"
      >
        <li role="none">
          <button
            role="menuitem"
            class="menu-item"
            @click="openProfile"
          >
            Mein Profil
          </button>
        </li>
        <li
          v-if="isAdmin"
          role="none"
        >
          <button
            role="menuitem"
            class="menu-item"
            @click="openSettings"
          >
            Einstellungen
          </button>
        </li>
        <li role="none">
          <button
            role="menuitem"
            class="menu-item danger"
            @click="onLogout"
          >
            Abmelden
          </button>
        </li>
      </ul>
    </div>

    <BaseDialog
      :open="profileOpen"
      @close="profileOpen = false"
    >
      <template #title>
        Mein Profil
      </template>
      <div class="form">
        <div class="field">
          <label>Name</label>
          <p class="muted">
            {{ displayName || '—' }}
          </p>
        </div>
        <div class="field">
          <label>E‑Mail</label>
          <p class="muted">
            {{ email || '—' }}
          </p>
        </div>
        <div
          class="mt-12"
          style="display:flex;justify-content:flex-end;"
        >
          <button
            class="btn"
            type="button"
            @click="profileOpen=false"
          >
            Schließen
          </button>
        </div>
      </div>
    </BaseDialog>

    <SchoolSettingsDialog
      :open="settingsOpen"
      @close="settingsOpen = false"
      @saved="onSettingsSaved"
    />
  </header>
  <div
    v-if="canSeeAlerts && releaseMandatoryCount > 0"
    class="top-nav-band notice notice-danger mb-3 text-center"
    role="status"
  >
    <span class="band-text">
      <strong>Achtung!</strong> Es gibt Schüler, die per Chip-Karte gegangen sind, aber noch Pflichtangebote haben.
    </span>
    <button
      class="btn"
      type="button"
      @click="goDashboard"
    >
      Anzeigen
    </button>
  </div>
</template>

<script setup>
import {computed, onMounted, onBeforeUnmount, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useAuthStore} from '@/stores/auth';
import {useAlertsStore} from '@/stores/alerts';
import BaseDialog from '@/components/BaseDialog.vue';
import SchoolSettingsDialog from '@/components/dialogs/SchoolSettingsDialog.vue';
// import sessionTimer from '@/services/sessionTimer';
import api from '@/services/api';

const emit = defineEmits(['toggle-sidebar']);
const {sidebarOpen} = defineProps({
  sidebarOpen: {type: Boolean, default: false}
});

const auth = useAuthStore();
const alerts = useAlertsStore();
const router = useRouter();

const menuOpen = ref(false);
const profileOpen = ref(false);
const settingsOpen = ref(false);
const rootEl = ref(null);

// Online-User für Admin anzeigen (Badges); Backend liefert nur nicht-abgelaufene Sessions
const onlineUsers = ref([]);
let onlineUsersTimer = null;

// Alerts polling
let alertsTimer = null;

function initialsOf(s) {
  const str = String(s || '').trim();
  if (!str) return 'U';
  const parts = str.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map(p => p.charAt(0).toUpperCase());
  return letters.join('') || str.charAt(0).toUpperCase();
}

function roleStringFromUser(u) {
  return u?.roleId;
}

function isTeacherUser(u) {
  const rid = Number(u?.roleId ?? u?.role_id);
  if (Number.isFinite(rid)) return rid === 2; // 2 = Lehrer
  const r = roleStringFromUser(u);
  return /(^|[,:\s_-])(?:teacher|lehrer)([,:\s_-]|$)/i.test(r);
}

function isParentUser(u) {
  const rid = Number(u?.roleId ?? u?.role_id);
  if (Number.isFinite(rid)) return rid === 3; // 3 = Eltern
  const r = roleStringFromUser(u);
  // Wenn keine eindeutige Lehrer-Rolle → als Eltern betrachten
  return /(^|[,:\s_-])(?:parent|eltern)([,:\s_-]|$)/i.test(r) || !isTeacherUser(u);
}

const parentsOnline = computed(() =>
    isAdmin.value
        ? (onlineUsers.value || []).filter(isParentUser)
        : []
);

const teachersOnline = computed(() =>
    isAdmin.value
        ? (onlineUsers.value || []).filter(isTeacherUser)
        : []
);

async function loadOnlineUsers() {
  if (!isAdmin.value) {
    onlineUsers.value = [];
    return;
  }

  try {
    const {data} = await api.get('/admin/online-users');
    const list = Array.isArray(data?.users) ? data.users : [];
    onlineUsers.value = list.map(u => ({
      id: u.id,
      name_code: u.name_code || '',
      display_name: u.display_name || '',
      isOnline: Boolean(u.isOnline ?? true), // Backend filtert bereits „offline“
      isRecent: Boolean(u.isRecent ?? false),
      roleId: u.roleId ?? '',
    }));
  } catch (e) {
    // stilles Ignorieren im Header
  }
}

const user = computed(() => auth.user || {});
const displayName = computed(() =>
    user.value?.displayName || user.value?.name || user.value?.nameCode || user.value?.email || 'Benutzer'
);
const email = computed(() => user.value?.email || '');
const initials = computed(() => {
  const s = (displayName.value || '').trim();
  if (!s) return 'U';
  const parts = s.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map(p => p.charAt(0).toUpperCase());
  return letters.join('') || s.charAt(0).toUpperCase();
});

const isAdmin = computed(() => {
  const u = user.value || {};

  // 1) Explizite Flags oder Store-Helper bevorzugen
  if (typeof auth?.isAdmin === 'boolean') return auth.isAdmin;
  if (typeof auth?.isAdmin === 'function') {
    const v = Boolean(auth.isAdmin());
    if (v) return true;
  }
  if (typeof auth?.hasRole === 'function') {
    const targets = ['ADMIN', 'ROLE_ADMIN', 'admin'];
    if (targets.some(t => !!auth.hasRole(t))) return true;
  }
  if (typeof auth?.hasPermission === 'function') {
    const perms = ['manage_school_settings', 'school_settings:write', 'settings:update'];
    if (perms.some(p => !!auth.hasPermission(p))) return true;
  }

  // 2) User-Flags
  if (u.isAdmin || u.is_admin || u.admin) return true;

  // 3) Rollen-/Scopes-/Permissions-Felder tolerant prüfen
  const fields = [u.roles, u.roleCodes, u.scopes, u.permissions, u.role_ids, u.roleIds];
  const list = fields.flatMap(r => Array.isArray(r) ? r : []);
  const strings = list.map(r => {
    if (typeof r === 'string') return r;
    if (typeof r === 'number') return String(r);
    return r?.code || r?.name || '';
  }).map(String);

  return strings.some(s => /(^|[_:\s-])admin(\b|$)/i.test(s));
});

const isTeacher = computed(() => {
  if (typeof auth?.isTeacher === 'boolean') return auth.isTeacher;
  if (typeof auth?.isTeacher === 'function') return Boolean(auth.isTeacher());
  const rid = Number(user.value?.roleId ?? user.value?.role_id ?? user.value?.role ?? 0);
  return Number.isFinite(rid) ? rid === 2 : false;
});

const canSeeAlerts = computed(() => isAdmin.value || isTeacher.value);
const releaseMandatoryCount = computed(() => Number(alerts.releaseMandatory?.count ?? 0) || 0);

async function loadAlerts() {
  if (!canSeeAlerts.value) return;
  await alerts.loadReleaseMandatoryAlerts();
}

function startAlertsPolling() {
  if (alertsTimer) return;

  // sofort initial laden, damit die Banderole ohne Delay korrekt ist
  loadAlerts();

  // dann jede Minute aktualisieren
  alertsTimer = setInterval(() => {
    loadAlerts();
  }, 60000);
}

function stopAlertsPolling() {
  if (!alertsTimer) return;
  clearInterval(alertsTimer);
  alertsTimer = null;
}

function goDashboard() {
  router.push('/dashboard');
}

function toggle() {
  menuOpen.value = !menuOpen.value;
}

function openProfile() {
  menuOpen.value = false;
  profileOpen.value = true;
}

function openSettings() {
  menuOpen.value = false;
  settingsOpen.value = true;
}

function onSettingsSaved() {
  // Optionaler Hook
}

function onLogout() {
  menuOpen.value = false;
  auth.logout();

  // lokaler Cleanup
  if (alertsTimer) {
    clearInterval(alertsTimer);
    alertsTimer = null;
  }
  alerts.releaseMandatory.count = 0;
  alerts.releaseMandatory.stayIds = [];
}

function onDocumentClick(e) {
  if (!menuOpen.value) return;
  const el = rootEl.value;
  if (el && !el.contains(e.target)) {
    menuOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, {capture: true});

  // Online-User (Admin)
  if (isAdmin.value) {
    loadOnlineUsers();
  }

  // Alerts werden über watch(canSeeAlerts) gestartet/gestoppt
});

// Reagiere auf Admin-Status: Polling starten/stoppen und Liste leeren
watch(() => isAdmin.value, (v) => {
  if (onlineUsersTimer) {
    clearInterval(onlineUsersTimer);
    onlineUsersTimer = null;
  }
  if (v) {
    loadOnlineUsers();
    onlineUsersTimer = setInterval(() => {
      if (isAdmin.value) loadOnlineUsers();
    }, 30000);
  } else {
    onlineUsers.value = [];
  }
}, {immediate: true});

// Reagiere auf Rollenänderung (Login/Logout): Alerts neu initialisieren
watch(() => canSeeAlerts.value, (v) => {
  if (!v) {
    stopAlertsPolling();
    alerts.releaseMandatory.count = 0;
    alerts.releaseMandatory.stayIds = [];
    return;
  }
  startAlertsPolling();
}, {immediate: true});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, {capture: true});
  if (onlineUsersTimer) {
    clearInterval(onlineUsersTimer);
    onlineUsersTimer = null;
  }
  stopAlertsPolling();
});
</script>

<style scoped>
.top-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  margin-bottom: 12px;
}

.grow {
  flex: 1;
}

.roles {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 24px;
}

.roles-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.roles-sep {
  width: 8px;
  height: 1px;
  opacity: 0.2;
}

.badge {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: inline-grid;
  place-items: center;
  font-size: 10px;
  font-weight: 400;
  color: #fff;
  background: var(--info);
}

.badge-teacher {
  background: var(--success);
}

.badge-parent {
  background: var(--info);
}

.badge.is-stale {
  background: var(--muted);
  opacity: 0.65;
}

.profile {
  position: relative;
}

.profile-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text);
  cursor: pointer;
}

.profile-btn:hover {
  border-color: var(--border-strong);
}

.profile-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: var(--primary);
  color: #fff;
  display: inline-grid;
  place-items: center;
  font-size: 12px;
  font-weight: 600;
}

.name {
  font-size: 14px;
}

.menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 180px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.12);
  padding: 6px;
  list-style: none;
  margin: 0;
  z-index: 10;
}

.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.menu-item:hover {
  background: rgba(79, 70, 229, 0.06);
}

.menu-item.danger:hover {
  background: rgba(239, 68, 68, 0.08);
}

/* Burger-Button: nur auf Mobile sichtbar */
.burger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
}

.burger:hover {
  border-color: var(--border-strong);
}

.burger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

@media (max-width: 1366px) {
  .burger {
    display: inline-flex;
  }
}

/* Nur für Screenreader sichtbarer Text */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 1px, 1px);
  white-space: nowrap;
  border: 0;
}
</style>
